import { useEffect, useRef, useState, type ReactNode } from "react";

export type BarcodeFigureFormat =
  | "upca"
  | "ean13"
  | "ean8"
  | "code128"
  | "code39"
  | "gs1128"
  | "itf14"
  | "qrcode"
  | "datamatrix"
  | "pdf417"
  | "aztec";

const FORMAT_LABEL: Record<BarcodeFigureFormat, string> = {
  upca: "UPC-A",
  ean13: "EAN-13",
  ean8: "EAN-8",
  code128: "Code 128",
  code39: "Code 39",
  gs1128: "GS1-128",
  itf14: "ITF-14",
  qrcode: "QR Code",
  datamatrix: "Data Matrix",
  pdf417: "PDF417",
  aztec: "Aztec",
};

export type BarcodeFigureProps = {
  format: BarcodeFigureFormat;
  /** Encoded value, e.g. "036000291452" for UPC-A. */
  value: string;
  /** Caption rendered under the barcode. Accepts inline markup. */
  caption?: ReactNode;
  /** Optional override of the small-label badge in the corner. */
  formatLabel?: string;
  /** Include the human-readable digits the spec normally prints. Defaults true. */
  includeText?: boolean;
  /** Visual scale multiplier (bwip-js module scale). Defaults 3. */
  scale?: number;
};

/**
 * Renders an example barcode inside a `<figure>` block so guide content can
 * make a point and show the symbol it's describing. Generation runs client-
 * side (bwip-js is browser-only on this site) — the figure reserves vertical
 * space during SSR / first paint to avoid layout shift, then swaps the SVG
 * in on hydration.
 */
export function BarcodeFigure({
  format,
  value,
  caption,
  formatLabel,
  includeText = true,
  scale = 3,
}: BarcodeFigureProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const slot = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function render() {
      try {
        if (format === "qrcode") {
          const QR = (await import("qrcode")).default;
          const raw = await QR.toString(value, { type: "svg", margin: 1, width: 220 });
          // The qrcode library emits explicit width / height attributes on the
          // <svg> root, which fight the CSS max-height that bounds the figure
          // slot. Strip them so the browser sizes from the viewBox aspect
          // ratio (square) and respects max-h + max-w consistently.
          const out = raw
            .replace(/\s(width|height)="[^"]*"/g, "")
            .replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet"');
          if (!cancelled) setSvg(out);
          return;
        }
        const bwipjs = (await import("bwip-js/browser")).default;
        const bcid =
          format === "upca"
            ? "upca"
            : format === "ean13"
              ? "ean13"
              : format === "ean8"
                ? "ean8"
                : format === "code128"
                  ? "code128"
                  : format === "code39"
                    ? "code39"
                    : format === "gs1128"
                      ? "gs1-128"
                      : format === "itf14"
                        ? "itf14"
                        : format === "pdf417"
                          ? "pdf417"
                          : format === "aztec"
                            ? "azteccode"
                            : "datamatrix";
        // bwip-js's gs1-128 symbology accepts the bracketed AI form directly
        // (e.g. "(01)07640156790000(10)ABC123") and converts the brackets to
        // FNC1 in the actual bar pattern while printing the readable AIs
        // below.
        const out = bwipjs.toSVG({
          bcid,
          text: value,
          scale,
          includetext: includeText,
          textxalign: "center",
        } as Parameters<typeof bwipjs.toSVG>[0]);
        if (!cancelled) setSvg(out);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to render");
      }
    }
    void render();
    return () => {
      cancelled = true;
    };
  }, [format, value, includeText, scale]);

  const badge = formatLabel ?? FORMAT_LABEL[format];

  return (
    <figure>
      <div className="flex flex-col items-start gap-2">
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
          <span className="shrink-0 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {badge}
          </span>
          <code className="block min-w-0 max-w-full break-all rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-snug text-foreground">
            {value}
          </code>
        </div>
        <div
          ref={slot}
          // Cap the height so wide barcodes (PDF417, multi-AI GS1-128) shrink
          // to fit the figure width without letterboxing — and the slot owns
          // a min-h so SSR layout doesn't jump during hydration. Width and
          // height both bounded by their parents; the SVG's intrinsic
          // viewBox aspect ratio drives the actual rendering.
          className="flex min-h-[140px] w-full items-center justify-center [&_svg]:block [&_svg]:max-h-[140px] [&_svg]:max-w-full"
          aria-label={`Example ${badge} barcode encoding ${value}`}
          role="img"
        >
          {svg ? (
            <div
              className="contents"
              // SVG is generated by trusted code from a controlled value —
              // safe to inject. No user-supplied HTML enters this path.
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ) : error ? (
            <span className="text-xs text-destructive">{error}</span>
          ) : (
            <span className="text-xs text-muted-foreground">Rendering example…</span>
          )}
        </div>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}
